'use client';
import * as React from 'react';
import { useMatch } from '@mentimeter/ragnar-device';
import {
  ChevronDownIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  MentiKHeart,
  MentimeterLogoType,
  TwitterIcon,
  YoutubeIcon,
} from '@mentimeter/ragnar-visuals';
import { useTranslations } from 'next-intl';
import { useDevelopmentToolsModal } from '@mentimeter/development-tools/hooks';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Collapsable } from '@mentimeter/ragnar-ui/collapsable';
import { List } from '@mentimeter/ragnar-ui/list';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Section } from '../../Layout/Section';
import { Wide } from '../../Layout/Wide';
import { Clickable } from '../../Wrapper/Clickable';
import { Link } from '../../Wrapper/Link';
import { H3 } from '../../Typography/H3';
import { FooterSwitchLanguage } from './FooterSwitchLanguage';
import { useDefaultFooterItems } from './hooks/useDefaultFooterItems';

export const Footer = () => {
  const { tryOpenDevelopmentToolsModal } = useDevelopmentToolsModal();

  const isMobile = useMatch({ lessThan: 3 });
  const [showMenu, setShowMenu] = React.useState<string>();

  const openSelectedMenu = (heading: string) => {
    setShowMenu(showMenu === heading ? undefined : heading);
  };

  const t = useTranslations('first_impression.legacy.common');
  const translations = useDefaultFooterItems();

  if (isMobile) {
    return (
      <Section
        theme={{ colors: 'plain' }}
        as="footer"
        justifyContent="center"
        bg="bg"
      >
        <Wide
          alignItems={['stretch', 'center']}
          flexDirection="column"
          mx="auto"
        >
          <Box my="space8" alignItems="flex-start" width="170px" height="23px">
            <MentimeterLogoType />
          </Box>
          <Box
            alignItems="stretch"
            flexDirection={['column', 'row']}
            width={['100%', 4 / 6]}
            justifyContent="space-around"
          >
            {translations.map(({ heading, links }) => (
              <Box key={heading} mb={['space4', 'space0']} alignItems="stretch">
                <Clickable
                  onClick={() => openSelectedMenu(heading)}
                  flexDirection="row"
                  aria-expanded={heading === showMenu}
                >
                  <Box
                    flexDirection="row"
                    justifyContent="space-between"
                    borderColor="text"
                    borderBottomWidth="1px"
                    borderStyle="solid"
                    width="100%"
                  >
                    <Text fontWeight="semiBold">{heading}</Text>
                    <Box
                      mt="space1"
                      extend={() => ({
                        transition: 'transform 0.2s ease',
                        transform:
                          showMenu === heading
                            ? 'rotate(-180deg)'
                            : 'rotate(0)',
                      })}
                    >
                      <ChevronDownIcon color="text" size={2} />
                    </Box>
                  </Box>
                </Clickable>
                {showMenu === heading && (
                  <Collapsable show={heading === showMenu} width="100%">
                    <List>
                      {links.map(({ href, children, ...rest }, i) => (
                        <Box as="li" key={href || 'footerButton_' + i}>
                          <Link
                            fontSize="100"
                            {...rest}
                            mb="space2"
                            color="textWeak"
                            tabIndex={showMenu ? 0 : -1} // If menu is hidden, links should not be focusable
                            underline={false}
                            href={href}
                            extend={() => ({
                              '&:first-child': {
                                marginTop: '16px',
                              },
                            })}
                          >
                            {children}
                          </Link>
                        </Box>
                      ))}
                    </List>
                  </Collapsable>
                )}
              </Box>
            ))}
          </Box>
          <FooterSwitchLanguage />
          <Box
            width={['100%', 1 / 6]}
            justifyContent="flex-end"
            alignItems="center"
            mb="space2"
          >
            <Box
              width="60px"
              height="60px"
              onClick={tryOpenDevelopmentToolsModal}
            >
              <MentiKHeart />
            </Box>
            <Box
              flexDirection="row"
              mt="space4"
              mb={['space8', 'space0']}
              justifyContent="center"
              width="100%"
            >
              <Clickable
                aria-label={t('page_links.facebook_aria')}
                href="https://www.facebook.com/Mentimeter"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FacebookIcon isWrappedInLink={true} size={4} />
              </Clickable>
              <Clickable
                aria-label={t('page_links.linkedin_aria')}
                href="https://www.linkedin.com/company/mentimeter"
                target="_blank"
                rel="noopener noreferrer"
                ml="space4"
              >
                <LinkedInIcon isWrappedInLink={true} size={4} />
              </Clickable>
              <Clickable
                aria-label={t('page_links.youtube_aria')}
                href="https://www.youtube.com/@Mentimeter"
                target="_blank"
                rel="noopener noreferrer"
                ml="space4"
              >
                <YoutubeIcon isWrappedInLink={true} size={4} />
              </Clickable>
              <Clickable
                aria-label={t('page_links.instagram_aria')}
                href="https://instagram.com/Mentimeter"
                target="_blank"
                rel="noopener noreferrer"
                ml="space4"
              >
                <InstagramIcon isWrappedInLink={true} size={4} />
              </Clickable>
              <Clickable
                aria-label={t('page_links.twitter_aria')}
                href="https://twitter.com/Mentimeter"
                target="_blank"
                rel="noopener noreferrer"
                ml="space4"
              >
                <TwitterIcon isWrappedInLink={true} size={4} />
              </Clickable>
            </Box>
          </Box>
        </Wide>
      </Section>
    );
  } else {
    return (
      <Section
        theme={{ colors: 'plain' }}
        as="footer"
        justifyContent="center"
        bg="bg"
      >
        <Wide alignItems="stretch" flexDirection="row" mx="auto">
          <Box width={1 / 5} alignItems="center" justifyContent="space-between">
            <Box width="170px" height="23px">
              <MentimeterLogoType />
            </Box>
            <Box
              justifyContent="flex-end"
              alignItems="center"
              mb="space2"
              width="100%"
            >
              <Box
                width="60px"
                height="60px"
                onClick={tryOpenDevelopmentToolsModal}
              >
                <MentiKHeart />
              </Box>
              <Box
                borderColor="text"
                borderBottomWidth="1px"
                borderStyle="solid"
              >
                <Box
                  flexDirection="row"
                  justifyContent="stretch"
                  width="100%"
                  my="space4"
                >
                  <Clickable
                    aria-label={t('page_links.facebook_aria')}
                    href="https://www.facebook.com/Mentimeter"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FacebookIcon isWrappedInLink={true} size={4} />
                  </Clickable>
                  <Clickable
                    aria-label={t('page_links.linkedin_aria')}
                    href="https://www.linkedin.com/company/mentimeter"
                    target="_blank"
                    rel="noopener noreferrer"
                    ml="space4"
                  >
                    <LinkedInIcon isWrappedInLink={true} size={4} />
                  </Clickable>
                  <Clickable
                    aria-label={t('page_links.youtube_aria')}
                    href="https://www.youtube.com/@Mentimeter"
                    target="_blank"
                    rel="noopener noreferrer"
                    ml="space4"
                  >
                    <YoutubeIcon isWrappedInLink={true} size={4} />
                  </Clickable>
                  <Clickable
                    aria-label={t('page_links.instagram_aria')}
                    href="https://instagram.com/Mentimeter"
                    target="_blank"
                    rel="noopener noreferrer"
                    ml="space4"
                  >
                    <InstagramIcon isWrappedInLink={true} size={4} />
                  </Clickable>
                  <Clickable
                    aria-label={t('page_links.twitter_aria')}
                    href="https://twitter.com/Mentimeter"
                    target="_blank"
                    rel="noopener noreferrer"
                    ml="space4"
                  >
                    <TwitterIcon isWrappedInLink={true} size={4} />
                  </Clickable>
                </Box>
              </Box>
              <FooterSwitchLanguage />
            </Box>
          </Box>
          <Box
            alignItems="stretch"
            flexDirection="row"
            width={4 / 5}
            justifyContent="space-around"
          >
            {translations.map(({ heading, links }) => (
              <Box key={heading}>
                <Box
                  borderColor="text"
                  borderBottomWidth="1px"
                  borderStyle="solid"
                  width="100%"
                  mb="space4"
                >
                  <H3
                    color="textWeak"
                    fontSize={['100']}
                    mb="space0"
                    lineHeight="normal"
                    fontWeight="semiBold"
                  >
                    {heading}
                  </H3>
                </Box>

                <List>
                  {links.map(({ href, children, ...rest }, i) => (
                    <Box as="li" key={href || 'footerButton_' + i}>
                      <Link
                        fontSize="100"
                        {...rest}
                        mb="space2"
                        color="textWeak"
                        underline={false}
                        href={href}
                      >
                        {children}
                      </Link>
                    </Box>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        </Wide>
      </Section>
    );
  }
};
